import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, Phone, User, CalendarBlank, Clock, ChatTeardropText, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('contact');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', is_anonymous: false });
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  // Auto-fill forms when user is logged in
  useEffect(() => {
    if (user) {
      setContactForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
      setBookingForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      setSuccess(response.data.message);
      setContactForm({ name: user?.name || '', email: user?.email || '', message: '', is_anonymous: false });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/booking`, bookingForm);
      setSuccess(response.data.message);
      setBookingForm({ name: user?.name || '', email: user?.email || '', phone: '', preferred_date: '', preferred_time: '', reason: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousMessage = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/anonymous-message`, { message: contactForm.message });
      setSuccess(response.data.message);
      setContactForm({ ...contactForm, message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#F9F6F0]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-[#2A2825] mb-4">
            Reach Out to Adeseun
          </h2>
          <p className="text-lg text-[#5C564F] max-w-2xl mx-auto">
            Whether you want to talk, book a session, or just share what's on your mind — I'm here for you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#B46C4A] rounded-3xl p-8 text-white"
          >
            <h3 className="text-2xl font-['Outfit'] font-semibold mb-6">Contact Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={24} weight="fill" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Email</p>
                  <a href="mailto:Adeseun.a@yahoo.com" className="hover:underline font-medium" data-testid="contact-email">
                    Adeseun.a@yahoo.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={24} weight="fill" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Phone / WhatsApp</p>
                  <a href="tel:07480927718" className="hover:underline font-medium" data-testid="contact-phone">
                    07480927718
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/70 text-sm leading-relaxed">
                Available for calls and messages Monday to Friday, 9 AM - 8 PM. For urgent support, please use the crisis resources in our Resource Library.
              </p>
            </div>

            {/* Decorative shape */}
            <div className="mt-8 opacity-20">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/188005d7-e4ad-4124-adeb-36b0fb46f144/images/e37e7f94b7f917192b8bfdf7e5a3cb418b3ba9b8c10e444e1916657ce3a0fbab.png"
                alt=""
                className="w-32 h-32 ml-auto"
              />
            </div>
          </motion.div>

          {/* Forms Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-white rounded-3xl p-8 border border-[#E5DCD0]"
          >
            {/* Tabs */}
            <div className="flex gap-2 mb-8 bg-[#F2EBE1] p-1 rounded-full overflow-x-auto">
              {[
                { id: 'contact', label: 'Message', icon: ChatTeardropText },
                { id: 'booking', label: 'Book', icon: CalendarBlank },
                { id: 'anonymous', label: 'Anonymous', icon: User }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSuccess(null); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-3 px-2 sm:px-4 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white text-[#2A2825] shadow-sm'
                      : 'text-[#5C564F] hover:text-[#2A2825]'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Success Message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 text-green-700 p-4 rounded-2xl mb-6 flex items-center gap-3 border border-green-100"
                data-testid="success-message"
              >
                <CheckCircle size={24} weight="fill" />
                {success}
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 text-red-700 p-4 rounded-2xl mb-6 border border-red-100"
                data-testid="error-message"
              >
                {error}
              </motion.div>
            )}

            {/* Contact Form */}
            {activeTab === 'contact' && (
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                {/* Show logged-in user info badge */}
                {user && (
                  <div className="bg-[#647B64]/10 text-[#647B64] px-4 py-3 rounded-2xl text-sm flex items-center gap-2">
                    <CheckCircle size={18} weight="fill" />
                    <span>Sending as <strong>{user.name}</strong> ({user.email})</span>
                  </div>
                )}
                
                {/* Only show name/email fields if NOT logged in */}
                {!user && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Your Name</label>
                      <input
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Enter your name"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                        data-testid="contact-name-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Email Address</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                        data-testid="contact-email-input"
                        required
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#5C564F] mb-2">Your Message</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="What would you like to share or discuss?"
                    rows={5}
                    className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A] resize-none"
                    data-testid="contact-message-input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all duration-300"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : <><PaperPlaneTilt size={20} weight="fill" /> Send Message</>}
                </button>
              </form>
            )}

            {/* Booking Form */}
            {activeTab === 'booking' && (
              <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="booking-form">
                {/* Show logged-in user info badge */}
                {user && (
                  <div className="bg-[#647B64]/10 text-[#647B64] px-4 py-3 rounded-2xl text-sm flex items-center gap-2">
                    <CheckCircle size={18} weight="fill" />
                    <span>Booking as <strong>{user.name}</strong> ({user.email})</span>
                  </div>
                )}
                
                {/* Only show name/email fields if NOT logged in */}
                {!user && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Your Name</label>
                      <input
                        type="text"
                        value={bookingForm.name}
                        onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                        placeholder="Enter your name"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3"
                        data-testid="booking-name-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Email Address</label>
                      <input
                        type="email"
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                        placeholder="your@email.com"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3"
                        data-testid="booking-email-input"
                        required
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#5C564F] mb-2">Phone (optional)</label>
                  <input
                    type="tel"
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    placeholder="Your phone number"
                    className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3"
                    data-testid="booking-phone-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#5C564F] mb-2">Preferred Date</label>
                    <div className="relative">
                      <CalendarBlank size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C564F]" />
                      <input
                        type="date"
                        value={bookingForm.preferred_date}
                        onChange={(e) => setBookingForm({ ...bookingForm, preferred_date: e.target.value })}
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl pl-12 pr-4 py-3"
                        data-testid="booking-date-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5C564F] mb-2">Preferred Time</label>
                    <div className="relative">
                      <Clock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C564F]" />
                      <select
                        value={bookingForm.preferred_time}
                        onChange={(e) => setBookingForm({ ...bookingForm, preferred_time: e.target.value })}
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl pl-12 pr-4 py-3 appearance-none"
                        data-testid="booking-time-input"
                        required
                      >
                        <option value="">Select time</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="19:00">7:00 PM</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5C564F] mb-2">What would you like to discuss?</label>
                  <textarea
                    value={bookingForm.reason}
                    onChange={(e) => setBookingForm({ ...bookingForm, reason: e.target.value })}
                    placeholder="Share briefly what's on your mind (optional but helpful)"
                    rows={4}
                    className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 resize-none"
                    data-testid="booking-reason-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all duration-300"
                  data-testid="booking-submit-btn"
                >
                  {loading ? 'Submitting...' : <><CalendarBlank size={20} weight="fill" /> Request Session</>}
                </button>
              </form>
            )}

            {/* Anonymous Form */}
            {activeTab === 'anonymous' && (
              <form onSubmit={handleAnonymousMessage} className="space-y-6" data-testid="anonymous-form">
                <div className="bg-[#F2EBE1] rounded-2xl p-4 text-sm text-[#5C564F]">
                  <p className="font-medium text-[#2A2825] mb-1">Your identity stays private</p>
                  <p>Share what's on your mind without revealing who you are. Sometimes it's easier to open up when you feel completely safe.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5C564F] mb-2">Your Message</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="What would you like to share? You're safe here..."
                    rows={6}
                    className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 resize-none"
                    data-testid="anonymous-message-input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#647B64] hover:bg-[#546854] disabled:bg-[#E5DCD0] text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all duration-300"
                  data-testid="anonymous-submit-btn"
                >
                  {loading ? 'Sending...' : <><User size={20} weight="fill" /> Send Anonymously</>}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
